import { ImageUploadUpdaterContext } from "@/contexts/ImageUploadUpdaterContext";
import { Box, Button, Dialog, DialogContent, DialogTitle, LinearProgress, Paper, styled, Typography, useTheme } from "@mui/material";
import { IFormSubmit, IFormValidate, ValidateOn } from "@ngt/forms-core";
import { FileUpload, Form, FormSubmitButton, GroupedField, GroupedFields, IFormLabel, IFormValidationError, ILookup, OnlinePatientManagementContext, Select, useSnackbar, ValidationErrorType } from "@ngt/opms";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GetImageUploadById, ImageDocument, ImageUpload, LookupTimePoints, PostSubmitImageUpload } from "../../api/dtos";
import { AppSettings } from "../../AppSettings";
import MriImageUploadsContext from "../../hooks/context/MriImageUploadsContext";
import useGetImageUploadById from "../../hooks/data/useGetImageUploadById";


const StyledButtonGroup = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 2, 2, 2),
    textAlign: 'center',
    gap: theme.spacing(2),
}))

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    padding: theme.spacing(2),

    '&>p': {
        padding: theme.spacing(0),
        fontSize: '1.25rem',
        fontWeight: 500
    }
}))

const StyledDialogContent = styled(DialogContent)(({theme}) => ({
    padding: theme.spacing(0, 0),
    overflow: 'visible',

    '&>p': {
        padding: `${theme.spacing(0, 2, 2, 2)} !important`,
    }
}));


interface IImageUploadDialog {
    dialogOpen: boolean;
    setDialogOpen: (open: boolean) => void;
    imageUploadId: number | undefined;
    setImageUploadId: (mriImageUploadId: number | undefined) => void;
   
    createNew?: boolean;
  
    imageDocumentId?: number;
}


const ImageUploadDialog: FC<IImageUploadDialog> = ({
    dialogOpen,
    setDialogOpen,
    imageUploadId: tempId,
    setImageUploadId,
    createNew = false,
    imageDocumentId
}) => {


    const theme = useTheme();

    const { serviceStackClient: client } = useContext(OnlinePatientManagementContext);
    const { enqueueSnackbar } = useSnackbar();

    const { data: imageUpload, loading } = useGetImageUploadById(tempId);

    const { mutate } = useContext(MriImageUploadsContext);

    
    const [updatedImageUpload, setUpdatedImageUpload] = useState<ImageUpload | undefined>(imageUpload);

    const imageUploadUpdater = useContext(ImageUploadUpdaterContext)

    useEffect(() => {
        if (imageUpload) {
            setUpdatedImageUpload(imageUpload);
        }
    }, [imageUpload]);

     useEffect(() => {
        if (!imageUpload?.imageDocument?.id) {
            return;
        }

        const unsubscribe = imageUploadUpdater.subscribe(imageUpload.imageDocument.id, async () => {
            //load new info for image upload
            const response = await client.get(
                new GetImageUploadById({
                    id: imageUpload.id,
                })
            )
            
            setUpdatedImageUpload(response.data);
        });

        return () => {
            unsubscribe();
        }
    }, [imageUploadUpdater, imageUpload?.imageDocument?.id]);
    

    const formInitialValue = useMemo<ImageUpload | undefined>(() => {
        if (!updatedImageUpload) {
            return updatedImageUpload;
        }

        if (createNew) {
            // add default document to the upload so the form has something to bind to.
            const newDocument: ImageDocument = {
                id: undefined,
                file: undefined,
                lookupTimePoints: LookupTimePoints.Baseline,
                name: "",
                enteredBy: "",
                modifiedBy: ""
            };

            return {
                ...updatedImageUpload,
                mriDocuments: [newDocument, ...(updatedImageUpload.mriDocuments ?? [])]
            };


        }

        if (imageDocumentId != null) {
            const target = updatedImageUpload.mriDocuments?.find(d => d.id === imageDocumentId);

            if (target) {
                return { ...updatedImageUpload, mriDocuments: [target] };
            }
        }

        return updatedImageUpload;
    }, [updatedImageUpload, createNew, imageDocumentId]);

    const closeDialog = useCallback(() => {
        setDialogOpen(false);
        // setImageUploadId(undefined);
    }, [setDialogOpen, setImageUploadId]);


    const [formSaving, setFormSaving] = useState(false);

   

    const onImageUploadSubmit: IFormSubmit<ImageUpload, IFormValidationError> = useCallback(async (form) => {

        setFormSaving(true);

        if (!form.value) {
            throw new Error("Form data is missing.");
        }

        try {
            await client.post(
                new PostSubmitImageUpload({
                    mriImageUpload: { ...form.value },
                })
            );

            await mutate(undefined, { revalidate: true });

            setFormSaving(false);

            closeDialog();

            enqueueSnackbar(
                <>
                    <Typography variant="subtitle1">
                        Upload Successful
                    </Typography>
                    This upload will be available after it has been de-identified.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            setFormSaving(false);

            enqueueSnackbar(
                <>
                    <Typography variant="subtitle1">
                        Upload Failed
                    </Typography>
                    An error occurred while attempting to upload the file. {error.message}
                </>,
                { variant: 'critical' }
            );
        }

    }, [client, enqueueSnackbar, closeDialog, mutate]);



    const imageUploadValidate: IFormValidate<ImageUpload, IFormValidationError> = useCallback(async (formState) => {

        let errors: Record<string, IFormValidationError[]> = {};

        const doc = formState.value?.mriDocuments?.[0];
        console.log('validating', formState.value, doc);

        if (doc?.lookupTimePoints == null) {
            errors['mriDocuments.0.lookupTimePoints'] = [
                {
                    property: 'mriDocuments.0.lookupTimePoints',
                    code: 'IU-006',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: ValidationErrorType.Normal
                }
            ];
        }

        if (doc?.file === undefined) {
            errors['mriDocuments.0.file'] = [
                {
                    property: 'mriDocuments.0.file',
                    code: 'IU-002',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: ValidationErrorType.Normal
                }
            ];
        }

        return errors;
    }, []);

    const imageUploadFieldsLookups: ILookup[] = useMemo(() => [
        {
            propertyName: 'mriDocuments.0.lookupTimePoints',
            items: [
                { id: LookupTimePoints.Baseline, value: 'Baseline' },
                { id: LookupTimePoints.EighteenMonth, value: '18 Month' },
            ]
        }
    ], []);

    const imageUploadFieldsLabels: IFormLabel[] = [
        {
            propertyPath: 'mriDocuments.0.lookupTimePoints',
            label: 'Timepoint',
            detailedLabel: 'Timepoint',
        },
        {
            propertyPath: 'mriDocuments.0.file',
            label: 'Upload',
            detailedLabel: 'Upload',
        },
    ];


    return <>
        <Dialog
            open={dialogOpen === true}
            onClose={closeDialog}
            aria-labelledby="start-dialog-title"
            aria-describedby="start-dialog-description"
            maxWidth="md"
            fullWidth
        >
            <Paper>
                <StyledDialogTitle id="start-dialog-title">
                    <Typography
                        variant="h3"
                        component="span"
                    >
                        Upload MRI {!(updatedImageUpload === undefined || loading == true) && <>(Participant {updatedImageUpload?.participantNumber})</>}
                    </Typography>
                </StyledDialogTitle>
                <StyledDialogContent>
                    {
                        updatedImageUpload === undefined || loading == true ? (
                            <LinearProgress
                                sx={{
                                    width: '100%',
                                    margin: theme.spacing(2, 0),
                                }}
                            />
                        ) : (
                            <>
                                <Form
                                    initialValue={formInitialValue}
                                    onSubmit={onImageUploadSubmit}
                                    lookups={imageUploadFieldsLookups}
                                    labels={imageUploadFieldsLabels}
                                    validateOn={ValidateOn.onChange}
                                    debug={AppSettings.debug}
                                    onValidate={imageUploadValidate}
                                >
                                    <GroupedFields>
                                        <GroupedField
                                            propertyPath={"mriDocuments.0.lookupTimePoints"}
                                            component={Select}
                                            FieldGridProps={{
                                                size: {
                                                    xs: 12,
                                                    sm: 12,
                                                    md: 6,
                                                    lg: 6,
                                                    xl: 6
                                                }
                                            }}
                                        />
                                        <GroupedField
                                            propertyPath={"mriDocuments.0.file"}
                                            component={FileUpload}
                                            FieldGridProps={{
                                                size: {
                                                    xs: 12,
                                                    sm: 12,
                                                    md: 6,
                                                    lg: 6,
                                                    xl: 6
                                                }
                                            }}
                                        />
                                    </GroupedFields>

                                    <StyledButtonGroup
                                        display="flex"
                                        justifyContent="flex-end"
                                    >
                                        <Button
                                            color="secondary"
                                            onClick={closeDialog}
                                            loading={formSaving}
                                        >
                                            Cancel
                                        </Button>

                                        <FormSubmitButton
                                            color="primary"
                                            variant="contained"
                                            loading={formSaving}
                                        >
                                            Submit
                                        </FormSubmitButton>

                                    </StyledButtonGroup>
                                </Form>
                            </>
                        )
                    }
                </StyledDialogContent>
            </Paper>
        </Dialog>
    </>
}

export default ImageUploadDialog;
